import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectProduct, setVizLoading } from '../../../../store/visualizerSlice';
import ProductList from '../../ProductList/ProductList';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './Generic.module.css';

export default function Generic({ 
    title = 'SELECT PRODUCT',
    seriesId, 
    surfaceName,
    stepIndex,
    room = '',
    onViewAllApplied, 
    onNext, 
    onPrevious 
}) {
    const dispatch = useDispatch();
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Selection key: Title_Room (e.g., Stone_Exterior)
    // Use title as step name since Generic is used for various steps
    const stepName = title.replace(/\s+/g, '');
    const selectionKey = `${stepName}_${room}`;

    // Restore selection from Redux on mount
    useEffect(() => {
        const savedSelection = stepSelections[selectionKey];
        if (savedSelection?.product) {
            setSelectedProduct(savedSelection.product);
        }
    }, [stepSelections, selectionKey]);

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        
        console.log('Generic - Product selected:', product.color || product.name);
        console.log('Generic - Applying to surface:', surfaceName);
        
        // Store selection in Redux with room-based key
        dispatch(selectProduct({
            product,
            surfaceName,
            stepIndex,
            selectionKey
        }));
        
        // Apply product to Chameleon visualization
        const chamViz = document.getElementById('chamViz');
        if (!chamViz) {
            console.error('Generic - chamViz element not found');
            return;
        }

        if (!product.id) {
            console.error('Generic - Product has no ID');
            return;
        }

        console.log('Generic - Applying product ID:', product.id);
        dispatch(setVizLoading(true));
        
        // Apply by surface name using product ID
        chamViz.applyByFilter(
            product.id, // Product ID, not specifier
            "",
            function(newSel) {
                // this = surface object { name, type, sortorder, sel }
                if (this.name === surfaceName) {
                    return newSel; // Apply the product
                }
                return this.sel; // Keep existing selection
            },
            true // auto-apply
        );
    };

    const handleViewAllApplied = () => {
        if (onViewAllApplied) {
            onViewAllApplied();
        }
    };

    return (
        <div className={styles.container}>
            <StepNavigation
                title={title}
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={handleViewAllApplied}
            />

            <div className={styles.productListContainer}>
                <ProductList
                    seriesId={seriesId}
                    thumbSize="256"
                    onProductSelect={handleProductSelect}
                    titleAttribute="color"
                    descriptionAttribute="name"
                    gridClassName={styles.productGrid}
                    selectedId={selectedProduct?.id}
                />
            </div>
        </div>
    );
}

Generic.propTypes = {
    title: PropTypes.string,
    seriesId: PropTypes.string.isRequired,
    surfaceName: PropTypes.string,
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
